import { useQuery } from '@tanstack/react-query'
import { Line, LineChart, ResponsiveContainer } from 'recharts'
import { accountApi } from '../api/client'
import type { Book } from '../types'
import './BookSparkline.css'

/**
 * Fleet-card equity sparkline. `book` is non-optional — this only renders
 * inside BookCard's `hasEquity` guard, where a book is already known to
 * exist. `book.id` MUST reach both the queryKey and accountApi.history: see
 * the scar comment on BookCard's own account query (BookCard.tsx ~L57) — a
 * bare key here would make every card share one cache entry and render
 * whichever book fetched last. `retry: false` matches that same query:
 * a dormant book 404s, which is a normal state (no line), not a fetch to
 * retry.
 */
export function BookSparkline({ book }: { book: Book }) {
  const { data } = useQuery({
    queryKey: ['equity-history', book.id, '1M'],
    queryFn: () => accountApi.history(book.id, '1M'),
    retry: false,
    staleTime: 300000,
  })

  const points = data?.points ?? []
  // One point is not a trend and would render as an invisible dot — and an
  // empty strip with a border reads as broken, so this returns nothing at
  // all rather than an empty container.
  if (points.length < 2) return null

  // equity arrives as a decimal STRING; Number() only at this boundary.
  const rows = points.map((p) => ({ t: p.t, equity: Number(p.equity) }))
  const first = rows[0].equity
  const last = rows[rows.length - 1].equity
  const stroke = last >= first ? 'var(--gain)' : 'var(--loss)'

  return (
    <div className="book-card-sparkline">
      <ResponsiveContainer width="100%" height={32}>
        <LineChart data={rows}>
          <Line type="monotone" dataKey="equity" stroke={stroke} strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
